import type { Cents } from './types.ts';

/** Shipping: flat $15, free once the pre-discount subtotal reaches $125. Canada only. */
export const SHIPPING_FLAT_CENTS: Cents = 1500;
export const FREE_SHIPPING_THRESHOLD_CENTS: Cents = 12500;
/**
 * Buy 6 get the 7th free: every full group of 7 cartons makes the cheapest one free.
 * Switched off 2026-10-11 by the owner ("remove that for now"); flip to true to bring it back,
 * every page and the checkout follow this flag.
 */
export const CARTON_DEAL_ENABLED = false;
export const DEAL_GROUP_SIZE = 7;

export interface CartLine {
  productId: string;
  unitCents: Cents;
  qty: number;
}

export interface Quote {
  cartonCount: number;
  subtotalCents: Cents;
  freeCartons: number;
  discountCents: Cents;
  shippingCents: Cents;
  totalCents: Cents;
  /** 0 when shipping is already free. */
  amountToFreeShippingCents: Cents;
  /** Cartons still needed to unlock the next free one; 0 when the deal is off. */
  cartonsToNextFree: number;
}

/**
 * The one pricing function. Same numbers in the cart, at checkout, in the Lambda that creates the
 * order and in the admin. Mirrors the WordPress behaviour exactly (cartonDeal.php, shippingRules.php):
 * the free-shipping threshold is checked on the subtotal BEFORE the carton discount.
 */
export function priceCart(lines: readonly CartLine[], opts: { dealEnabled?: boolean } = {}): Quote {
  const dealEnabled = opts.dealEnabled ?? CARTON_DEAL_ENABLED;
  let cartonCount = 0;
  let subtotalCents = 0;
  const units: Cents[] = [];
  for (const line of lines) {
    if (!Number.isInteger(line.qty) || line.qty < 0) throw new Error(`bad qty ${line.qty}`);
    if (!Number.isInteger(line.unitCents) || line.unitCents < 0) throw new Error(`bad price ${line.unitCents}`);
    cartonCount += line.qty;
    subtotalCents += line.qty * line.unitCents;
    for (let i = 0; i < line.qty; i++) units.push(line.unitCents);
  }
  const freeCartons = dealEnabled ? Math.floor(cartonCount / DEAL_GROUP_SIZE) : 0;
  units.sort((a, b) => a - b);
  const discountCents = units.slice(0, freeCartons).reduce((sum, c) => sum + c, 0);
  const shippingCents = cartonCount === 0 || subtotalCents >= FREE_SHIPPING_THRESHOLD_CENTS ? 0 : SHIPPING_FLAT_CENTS;
  const totalCents = subtotalCents - discountCents + shippingCents;
  return {
    cartonCount,
    subtotalCents,
    freeCartons,
    discountCents,
    shippingCents,
    totalCents,
    amountToFreeShippingCents: Math.max(0, FREE_SHIPPING_THRESHOLD_CENTS - subtotalCents),
    cartonsToNextFree: !dealEnabled ? 0 : cartonCount === 0 ? DEAL_GROUP_SIZE : DEAL_GROUP_SIZE - (cartonCount % DEAL_GROUP_SIZE),
  };
}
